import { defineStore } from "pinia";
import { computed, ref, type Ref } from "vue";
import { NewMaterial } from "@/types/Views/CreateMaterial/NewMaterial";
import { ExtractionClassification } from "@/services/Views/CreateMaterial/PdfExtractService";

type State = "create" | "edit" | "clone";

interface NewMaterialStore {
  // State
  classification: Ref<ExtractionClassification | null>;

  // Getters
  getMaterial: () => NewMaterial | null;
  getIsCustom: () => boolean;
  getFile: () => File;

  // Setters
  setMaterial: (newMaterial: NewMaterial) => void;
  setIsCustom: (newValue: boolean) => void;
  setFile: (newFile: File | undefined) => void;
  setState: (newState: State) => void;
  setClassification: (newClassification: ExtractionClassification | null) => void;

  // Methods
  isCreating: () => boolean;
  isEditing: () => boolean;
  isCloning: () => boolean;
  reset: () => void;

  // Dirty state (shared for the discard-confirm flow, mirrors NewConstructionStore)
  materialIsDirty: Ref<boolean>;
  initMaterialOriginal: () => void;
}

export const newMaterialStore = defineStore("newMaterialStore", (): NewMaterialStore => {
  // ///////////////////
  // Refs
  // ///////////////////

  const material = ref<NewMaterial | null>(null);
  const isCustom = ref<boolean>(false);
  const file = ref();
  const createMaterialPageState = ref<State>("create");
  // Multi-product / multi-scenario flags from the last PDF extraction; drives the review banner.
  const classification = ref<ExtractionClassification | null>(null);
  // Snapshot to diff against for the discard-confirm flow (RTLCA-5078). NewMaterial's shape is
  // large and evolves often, so a JSON comparison is used rather than a hand-maintained field diff
  // (unlike NewConstructionStore's getChangesFromOriginal, which only needs to cover ~6 fields).
  const originalMaterial = ref<NewMaterial | null>(null);

  // ///////////////////
  // Getters
  // ///////////////////

  const getMaterial = () => {
    return material.value;
  };

  const getIsCustom = () => {
    return isCustom.value;
  };

  const getFile = () => {
    return file.value;
  };

  // ///////////////////
  // Setters
  // ///////////////////

  const setMaterial = (newMaterial: NewMaterial) => {
    material.value = newMaterial;
  };

  const setIsCustom = (newValue: boolean) => {
    isCustom.value = newValue;
  };

  const setFile = (newFile: File | undefined) => {
    file.value = newFile;
  };

  const setState = (newState: State) => {
    createMaterialPageState.value = newState;
  };

  const setClassification = (newClassification: ExtractionClassification | null) => {
    classification.value = newClassification;
  };

  // ///////////////////
  // Methods
  // ///////////////////

  const isCreating = () => {
    return createMaterialPageState.value === "create";
  };

  const isEditing = () => {
    return createMaterialPageState.value === "edit";
  };

  const isCloning = () => {
    return createMaterialPageState.value === "clone";
  };

  function reset() {
    material.value = null;
    isCustom.value = false;
    file.value = undefined;
    createMaterialPageState.value = "create";
    classification.value = null;
    originalMaterial.value = null;
  }

  const materialIsDirty = computed(() => {
    if (!originalMaterial.value || !material.value) return false;
    return JSON.stringify(material.value) !== JSON.stringify(originalMaterial.value);
  });

  const initMaterialOriginal = () => {
    originalMaterial.value = material.value ? JSON.parse(JSON.stringify(material.value)) : null;
  };

  // ///////////////////
  // Watchers
  // ///////////////////

  return {
    // State
    classification,

    // Getters
    getMaterial,
    getIsCustom,
    getFile,

    // Setters
    setMaterial,
    setIsCustom,
    setFile,
    setState,
    setClassification,

    // Methods
    isCreating,
    isEditing,
    isCloning,
    reset,

    // Dirty state
    materialIsDirty,
    initMaterialOriginal,
  };
});
